import React, { useState, useEffect } from 'react';
import logoMark from '../assets/landing/logo-mark.png';

/**
 * Landing-page top navigation.
 * Only rendered on the landing page ("/"), so every link is an on-page
 * anchor scroll — no routing needed here.
 *
 * Behavior ported from the approved design:
 *  - transparent at the very top, gains a blurred dark background once
 *    you scroll past the hero (navSc)
 *  - the link for the section currently in view is highlighted green (navAct)
 */

const CTA_URL = '/download?source=nav';

// id in the DOM -> label in the nav
const LINKS = [
  { id: 'how-it-works', label: 'How it Works' },
  { id: 'spending', label: 'Spending' },
  { id: 'ask', label: 'Ask Your Spending' },
  { id: 'rewards', label: 'Rewards' },
];

export default function Nav({ mobile = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState('');

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);

      // find which section is currently under the top of the viewport
      let current = '';
      for (const { id } of LINKS) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 120) current = id;
      }
      setActive(current);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 70;
      window.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  };

  const toTop = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  return (
    <nav className="landing-nav"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '24px',
        padding: '20px clamp(20px,5vw,56px)',
        borderBottom: `1px solid ${scrolled ? 'var(--border-hairline)' : 'transparent'}`,
        position: 'sticky',
        top: 0,
        background: scrolled ? 'rgba(0,0,0,0.72)' : 'transparent',
        backdropFilter: scrolled ? 'blur(14px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(14px)' : 'none',
        zIndex: 50,
        transition: 'background 0.35s, border-color 0.35s',
      }}
    >
      <button
        type="button"
        onClick={toTop}
        aria-label="Back to top"
        style={{
          display: 'block',
          cursor: 'pointer',
          background: 'none',
          border: 0,
          padding: 0,
        }}
      >
        <img
          src={logoMark}
          alt="KACHNG"
          style={{ height: '30px', width: 'auto', display: 'block' }}
        />
      </button>

      <div className="landing-nav-links"
        style={{
          display: 'flex',
          gap: 'clamp(16px,3vw,36px)',
          fontSize: '14px',
          flexWrap: 'wrap',
        }}
      >
        {LINKS.map(({ id, label }) => (
          <a
            key={id}
            href={`#${id}`}
            onClick={(e) => scrollTo(e, id)}
            style={{
              color: active === id ? 'var(--kachng-green)' : 'var(--text)',
              transition: 'color 0.25s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--kachng-green)')}
            onMouseLeave={(e) =>
            (e.currentTarget.style.color =
              active === id ? 'var(--kachng-green)' : 'var(--text)')
            }
          >
            {label}
          </a>
        ))}
      </div>

      <a
        href={mobile ? '/download?source=mobile-nav' : CTA_URL}
        style={{
          background: 'var(--kachng-green)',
          color: '#000',
          fontWeight: 600,
          fontSize: '14px',
          padding: '10px 20px',
          borderRadius: '6px',
          flex: 'none',
        }}
      >
        Get KACHNG
      </a>
    </nav>
  );
}